export interface GeminiConfig {
  apiKey: string
  model?: string
}

export interface GeminiContent {
  role: 'user' | 'model'
  parts: GeminiPart[]
}

export interface GeminiPart {
  text?: string
  functionCall?: { name: string; args: Record<string, unknown>; id?: string }
  functionResponse?: { name: string; response: Record<string, unknown>; id?: string }
  inlineData?: { mimeType: string; data: string }
  fileData?: { mimeType: string; fileUri: string }
}

export interface GeminiRequest {
  contents: GeminiContent[]
  tools?: { functionDeclarations: GeminiFunctionDeclaration[] }[]
}

export interface GeminiFunctionDeclaration {
  name: string
  description: string
  parameters: Record<string, unknown>
}

export interface GeminiResult {
  text?: string
  functionCall?: { name: string; args: Record<string, unknown>; id?: string }
  functionCalls?: Array<{ name: string; args: Record<string, unknown>; id?: string }>
  raw: Record<string, unknown>
}

const BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/models'

export class GeminiClient {
  private apiKey: string
  private model: string

  constructor(config: GeminiConfig) {
    this.apiKey = config.apiKey
    this.model = config.model ?? 'gemini-2.5-pro'
  }

  async generateContentStream(
    request: GeminiRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ReadableStream<Uint8Array>> {
    const url = `${BASE_URL}/${this.model}:streamGenerateContent?alt=sse`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-goog-api-key': this.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: options?.signal,
    })
    if (!response.ok) {
      throw new Error(`Gemini stream error: ${response.status} ${response.statusText}`)
    }
    return response.body!
  }

  async generateContent(request: GeminiRequest): Promise<GeminiResult> {
    const url = `${BASE_URL}/${this.model}:generateContent`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-goog-api-key': this.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status} ${response.statusText}`)
    }

    const json = await response.json() as Record<string, unknown>
    const candidates = json.candidates as Array<Record<string, unknown>> | undefined
    const candidate = candidates?.[0]
    const content = candidate?.content as Record<string, unknown> | undefined
    const parts = (content?.parts ?? []) as Array<Record<string, unknown>>
    const firstPart = parts[0]

    // Collect all functionCall parts
    const fcParts = parts.filter((p) => p.functionCall != null)

    if (fcParts.length > 1) {
      // Multiple parallel function calls
      const calls = fcParts.map((p) => {
        const fc = p.functionCall as { name: string; args: Record<string, unknown>; id?: string }
        return fc
      })
      return {
        functionCall: calls[0],
        functionCalls: calls,
        raw: json,
      }
    }

    if (fcParts.length === 1) {
      const fc = fcParts[0].functionCall as { name: string; args: Record<string, unknown>; id?: string }
      return {
        functionCall: fc,
        raw: json,
      }
    }

    return {
      text: firstPart?.text as string | undefined,
      raw: json,
    }
  }
}
